<script lang="ts">
  import type { VoiceType } from 'shared/definitions/musicTheory/voiceTypes';
  import { onMount } from 'svelte';
  import Sky from './Sky.svelte';
  import Pier from './Pier.svelte';
  import Fish from './Fish.svelte';
  import {
    groundHeightCoefficient,
    pierIntervalTimeSeconds,
    pierOnScreenSeconds,
    skyHeightCoefficient,
    targetHeightCoefficient,
  } from './pitcyFishConstants';
  import Parallax from './Parallax.svelte';
  import { PierPhysics } from './PierPhysics';
  import type { PitchyFishGameStore } from './PitchyFishGameStore';
  import MicrophoneAccessRequest from './MicrophoneAccessRequest.svelte';
  import StartScreen from './StartScreen.svelte';
  import PitchyFishHighScore from './PitchyFishHighScore.svelte';
  import AwaitingPitchDetectionInitSplashScreen from './AwaitingPitchDetectionInitSplashScreen.svelte';
  /**
   *  I've adapted code from two codePens in this. One for the waves, and one for the bubbles.
   *  we started with some MIT licensed code for the game, but we've changed almost everything.
   *  Original MIT licensed code: https://github.com/kubowania/flappy-bird
   *  Bubbles codePen: https://codepen.io/ruhantai/pen/xBmEOX
   *  Waves codePen: https://codepen.io/goodkatz/pen/LYPGxQz
   *  Fish Sprite from https://www.gamedeveloperstudio.com/graphics/viewgraphic.php?page-name=Fish-sprite&item=134a143f33661k3o0l
   *  foreground purchased from  https://www.gamedeveloperstudio.com/graphics/viewgraphic.php?page-name=Underwater-background&item=154r5x9r3v4r219e99
   *
   * This is a massive rewrite of the codebase to allow for:
   * - A completely responsive container that will work on any device or in any window
   * - That will still retain the same aspect ratio on the main gameboard
   * - An animated underwater environment, with bubbles and waves.
   * - A simpler drawing and collision system that is more extensible. (Target based.)
   * - The ability to easily integrate the same dichord collection used by solfege sally.
   */
  const debug = false;
  /** width of containing window */
  let w: number;
  /** height of containing window */
  let h: number;
  let fish: Fish;
  /**
   * The ground and bubbles.
   */
  let parallax: Parallax;
  /** width of game window */
  let width: number;
  /** height of game window */
  let height: number;
  /** desired aspect ratio width/height */
  const yRatio = 500 / 730;
  /** desired aspect ratio height/width */
  const xRatio = 730 / 500;
  /** input from pitch detector */
  export let midi: number = -1;

  /** The user's voice type */
  export let voiceType: VoiceType;

  export let store: PitchyFishGameStore;

  /** gap between obstacles or height of targets. Set on window resize with height multiplier. */
  let startMoment = Date.now();

  let pierPhysicsInstances: PierPhysics[] = [];

  let pierPhysicsInstancesCoordinates: ReturnType<PierPhysics['getCoordinates']>[] = [];

  /** for debugging */
  const enableCollisionDetection = true;

  $: {
    if (w > h * yRatio) {
      // We are limited by width:
      height = h;
      width = h * yRatio;
    } else {
      // We are limited by height:
      width = w;
      height = w * xRatio;
    }
  }

  /** for debugging */
  let pauseLoop = false;
  (window as any).pause = function pauser() {
    pauseLoop = !pauseLoop;
  };
  export const pause = () => {
    pauseLoop = !pauseLoop;
  };

  function addObstacle() {
    if (pauseLoop) return;
    /** will always have at least .03% of barrier at top and bottom of visible sea. */
    const minTargetTop = skyHeightCoefficient;
    const maxTargetTop = 1 - groundHeightCoefficient - targetHeightCoefficient;
    const targetRange = maxTargetTop - minTargetTop;
    /** sets top of gap */
    const targetTopPercent = minTargetTop + Math.random() * targetRange;
    /** sets bottom of gap */
    const targetBottomPercent = targetTopPercent + targetHeightCoefficient;

    pierPhysicsInstances.push(
      new PierPhysics({
        containerWidth: width,
        containterHeight: height,
        secondsPerWidth: pierOnScreenSeconds,
        targetTopPercent,
        targetBottomPercent,
      }),
    );
  }

  let frames: number[] = [];
  let frameRate: number | undefined;
  function logFrame() {
    frames.push(Date.now());
    // Track only the last 80 frames
    frames = frames.slice(-20);
    const startFrame = frames[0];
    const endFrame = frames[frames.length - 1];
    if (typeof startFrame === 'undefined' || typeof endFrame === 'undefined') return;
    const totalTime = endFrame - startFrame;
    if (totalTime === 0) return;
    const averageFrameSpeed = totalTime / frames.length;
    frameRate = Math.round(1000 / averageFrameSpeed);
  }

  /** This is currently the main game loop. */
  function gameLoop() {
    logFrame();
    if (pauseLoop) return;

    fish.updatePosition();
    parallax.updateGroundAndBubblesPosition();

    pierPhysicsInstances = pierPhysicsInstances.filter((physics) => {
      physics.containerWidth = width;
      physics.containterHeight = height;
      const isOffScreen = physics.updateCoordinates().isOffScreen;
      if (isOffScreen) {
        store.incrementScore();
      }
      return !isOffScreen;
    });

    // Used to draw the piers
    pierPhysicsInstancesCoordinates = pierPhysicsInstances.map((physics) =>
      physics.getCoordinates(),
    );

    if (enableCollisionDetection) {
      const isCollision = !!pierPhysicsInstances.find((physics) =>
        physics.isCollision(fish.getYCoefficient()),
      );
      if (isCollision) {
        gameOver();
      }
    }

    if ($store.state === 'gameOver') {
      /** TODO: also save user range to preferences? */
    } else if (typeof window !== 'undefined') {
      window.requestAnimationFrame(gameLoop);
    }
  }

  let containerDiv: HTMLDivElement;

  onMount(() => {
    if (typeof window !== 'undefined' && containerDiv) {
      setTimeout(() => {
        const { y } = containerDiv.getBoundingClientRect();
        window.scrollTo({ top: y, left: 0 });
      }, 200);
    }
  });

  let addObstacleTimer: number;

  store.addEventListener('GameStarted', () => {
    pierPhysicsInstances = [];
    pierPhysicsInstancesCoordinates = [];
    startMoment = Date.now();
    addObstacle();
    addObstacleTimer = setInterval(
      addObstacle,
      pierIntervalTimeSeconds * 1000,
    ) as unknown as number;
    store.score = 0;
    gameLoop();
  });

  function gameOver() {
    clearInterval(addObstacleTimer as number);
    store.state = 'gameOver';
  }
</script>

<!-- Here is where we get the height and width of the window for responsive drawing within a fixed aspect ratio.-->
<svelte:window bind:innerHeight={h} bind:innerWidth={w} />

{#if debug}
  <p>Frame Rate: {frameRate}</p>
  <p>State: {$store.state}</p>
{/if}
<div id="container" bind:this={containerDiv}>
  <div id="game" style="height: {height}px; width: {width}px;">
    {#if $store.state === 'notStarted'}
      <StartScreen {height} on:startClicked={() => store.startGame()} bind:voiceType />
    {:else if $store.state === 'gameOver'}
      <StartScreen
        gameOver={true}
        {height}
        on:startClicked={() => store.startGame()}
        bind:voiceType
      />
      <PitchyFishHighScore userScore={$store.score} {height} userHighScore={$store.highScore} />
    {:else if $store.state === 'awaitingMicrophonePermission'}
      <MicrophoneAccessRequest {store} />
    {:else if $store.state === 'awaitingPitchDetectionInit'}
      <AwaitingPitchDetectionInitSplashScreen />
    {:else if $store.state === 'running'}
      <PitchyFishHighScore userScore={$store.score} {height} userHighScore={$store.highScore} />
    {:else if $store.state === 'errored'}
      <div class="pitchy-fish-start-screen" style="font-size: {0.1 * height}px;">
        <div id="gameOver">Uh-oh!!!</div>
        <p>Pitchy fish ran into an error. {$store.error}</p>
        <button id="restartButton" on:mousedown={() => store.startGame()}>Try Again!</button>
      </div>
    {/if}

    <Sky containerHeight={height} />
    <Parallax
      bind:this={parallax}
      secondsPerWidth={pierOnScreenSeconds}
      containerWidth={width}
      containerHeight={height}
      {startMoment}
    />

    {#each pierPhysicsInstancesCoordinates as { left, targetTop, targetBottom, targetWidth, height, id } (id)}
      <Pier {left} {targetTop} {targetBottom} {targetWidth} {height} />
    {/each}

    <Fish
      bind:this={fish}
      isVisible={true}
      containerWidth={width}
      containerHeight={height}
      lowestMidi={$store.lowestMidi}
      highestMidi={$store.highestMidi}
      {midi}
    />
  </div>
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Luckiest+Guy&display=swap');
  /** This is just the parent container. Gives ivory letterboxing to the game board */
  #container {
    line-height: initial;
    font-family: 'Luckiest Guy', Impact, 'Arial Narrow Bold', sans-serif;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    overflow: hidden;
    user-select: none;
  }
  #gameOver {
    font-family: 'Luckiest Guy', Impact, 'Arial Narrow Bold', sans-serif;
    color: #ffc400;
    top: 15%;
    position: absolute;
    text-shadow: 4px 4px 10px black;
  }
  #restartButton {
    font-family: 'Luckiest Guy', Impact, 'Arial Narrow Bold', sans-serif;
    padding: 0;
    text-align: center;
    /** padding-top seems to adjust for false vertical centering of this font. */
    padding-top: 5%;
    position: absolute;
    left: 10%;
    top: 45%;
    width: 80%;
    height: 35%;
    font-size: 100%;
    border-radius: 8%;
    color: black;
    background: linear-gradient(coral, rgb(217, 255, 0));
  }
  /** This is the game container, including "ground" "sea" and "sky" */
  #game {
    overflow: hidden;
    position: relative;
    margin: auto;
  }
  :global(.pitchy-fish-start-screen) {
    position: relative;
    margin: auto;
    top: 10%;
    height: 80%;
    width: 80%;
    text-align: center;
    z-index: 40;
  }
</style>
